/**
 * MyCopro - Constantes de configuration
 */

export type UserCategory = 'tenant' | 'owner' | 'council' | 'syndic' | 'gardien' | 'admin';

// Feature flag — passer à true pour activer le verrouillage biométrique
export const BIOMETRIC_LOCK_FEATURE_ENABLED = true;

// Configuration de l'application
export const APP_CONFIG = {
  name: "O'Copro",
  version: '1.0.0',
  defaultLanguage: 'fr',
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  subscriptionWarningDays: 10,
  subscriptionCriticalDays: 3
};

// Catégories d'utilisateurs
export const USER_CATEGORIES = {
  tenant: {
    id: 'tenant',
    label: 'Locataire',
    color: 'bg-teal-500',
    textColor: 'text-teal-700',
    bgLight: 'bg-teal-100'
  },
  owner: {
    id: 'owner',
    label: 'Copropriétaire',
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-100'
  },
  council: {
    id: 'council',
    label: 'Conseil Syndical',
    color: 'bg-purple-500',
    textColor: 'text-purple-700',
    bgLight: 'bg-purple-100'
  },
  syndic: {
    id: 'syndic',
    label: 'Syndic',
    color: 'bg-amber-500',
    textColor: 'text-amber-700',
    bgLight: 'bg-amber-100'
  },
  gardien: {
    id: 'gardien',
    label: 'Gardien',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-700',
    bgLight: 'bg-emerald-100'
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    color: 'bg-red-500',
    textColor: 'text-red-700',
    bgLight: 'bg-red-100'
  }
};

// Types d'événements
export const EVENT_TYPES = {
  maintenance: {
    id: 'maintenance',
    label: 'Maintenance',
    color: 'bg-orange-500',
    icon: 'Wrench'
  },
  works: {
    id: 'works',
    label: 'Travaux',
    color: 'bg-amber-500',
    icon: 'HardHat'
  },
  social: {
    id: 'social',
    label: 'Événement social',
    color: 'bg-pink-500',
    icon: 'PartyPopper'
  },
  ag: {
    id: 'ag',
    label: 'Assemblée Générale',
    color: 'bg-blue-500',
    icon: 'Users'
  },
  payment: {
    id: 'payment',
    label: 'Appel de fonds',
    color: 'bg-purple-500',
    icon: 'CreditCard'
  }
};

// Types de demandes
export const REQUEST_TYPES = {
  label: {
    id: 'label',
    title: 'Étiquette boîte aux lettres / digicode',
    color: 'bg-blue-100 text-blue-600',
    icon: 'FileText',
    placeholder: 'Ex: Nom et prénom pour étiquette BAL, numéro de digicode souhaité...'
  },
  fiber: {
    id: 'fiber',
    title: 'Accès local fibre internet',
    color: 'bg-purple-100 text-purple-600',
    icon: 'Wifi',
    placeholder: 'Ex: Installation Orange prévue le 15/03, besoin accès local technique...'
  },
  problem: {
    id: 'problem',
    title: 'Signalement de problème',
    color: 'bg-amber-100 text-amber-600',
    icon: 'AlertCircle',
    placeholder: 'Décrivez le problème et sa localisation précise...'
  }
};

// Groupes de chat
export const CHAT_GROUPS = {
  residence: {
    id: 'residence',
    name: 'Résidence',
    description: 'Discussion générale',
    color: 'bg-blue-500',
    icon: 'Building2'
  },
  owners: {
    id: 'owners',
    name: 'Copropriétaires',
    description: 'Échanges entre propriétaires',
    color: 'bg-purple-500',
    icon: 'Users'
  },
  council: {
    id: 'council',
    name: 'Conseil Syndical',
    description: 'Discussion privée du CS',
    color: 'bg-amber-500',
    icon: 'Shield'
  }
};

// Catégories de documents
export const DOCUMENT_CATEGORIES = {
  ag: {
    id: 'ag',
    label: 'Assemblées Générales',
    icon: 'Calendar',
    color: 'bg-blue-100 text-blue-600'
  },
  reglement: {
    id: 'reglement',
    label: 'Règlement',
    icon: 'Shield',
    color: 'bg-purple-100 text-purple-600'
  },
  travaux: {
    id: 'travaux',
    label: 'Travaux',
    icon: 'Building2',
    color: 'bg-amber-100 text-amber-600'
  },
  finances: {
    id: 'finances',
    label: 'Finances',
    icon: 'FileText',
    color: 'bg-green-100 text-green-600'
  },
  divers: {
    id: 'divers',
    label: 'Divers',
    icon: 'Folder',
    color: 'bg-gray-100 text-gray-600'
  }
};

// Statuts des demandes
export const REQUEST_STATUS = {
  pending: {
    id: 'pending',
    label: 'En attente',
    color: 'bg-amber-100 text-amber-700',
    icon: 'Clock'
  },
  validated: {
    id: 'validated',
    label: 'Validée',
    color: 'bg-green-100 text-green-700',
    icon: 'CheckCircle'
  },
  transmitted: {
    id: 'transmitted',
    label: 'Transmise',
    color: 'bg-blue-100 text-blue-700',
    icon: 'Send'
  },
  rejected: {
    id: 'rejected',
    label: 'Refusée',
    color: 'bg-red-100 text-red-700',
    icon: 'XCircle'
  },
  closed: {
    id: 'closed',
    label: 'Clôturée',
    color: 'bg-gray-100 text-gray-700',
    icon: 'Archive'
  },
  cancelled: {
    id: 'cancelled',
    label: 'Annulée',
    color: 'bg-orange-100 text-orange-700',
    icon: 'XCircle'
  }
};

// Monitoring Actions
export const MONITORING_ACTIONS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  VIEW_DOCUMENT: 'VIEW_DOCUMENT',
  DOWNLOAD_DOCUMENT: 'DOWNLOAD_DOCUMENT',
  CREATE_REQUEST: 'CREATE_REQUEST',
  VOTE_POLL: 'VOTE_POLL',
  SEND_MESSAGE: 'SEND_MESSAGE',
  VIEW_PAGE: 'VIEW_PAGE'
};

// Screen Names
export const SCREEN_NAMES = {
  HOME: 'Accueil',
  INFO: 'Informations',
  CALENDAR: 'Calendrier',
  REQUESTS: 'Demandes',
  DOCUMENTS: 'Documents',
  POLLS: 'Sondages',
  CHAT: 'Messagerie',
  NOTIFICATIONS: 'Notifications',
  PROFILE: 'Profil',
  SETTINGS: 'Parametres'
};